import { z } from 'zod';
import type { Locale } from './locale.js';
import type { DisplayNameLock } from './verification.js';

/**
 * F07 user identity types. The `users` table is separate from F06 `admin_users`;
 * see plans/07_F07_user_authentication.md for the full decision context.
 */

export type UserRole =
  | 'consumer'
  | 'venue_owner'
  | 'trainer'
  | 'club_admin'
  // F41 — tournament/event organizer (federations, leagues, independent event
  // companies). Granted by `OrganizerService.adminApprove` when an admin
  // approves an `account_kind='organizer'` partner. This is mainly an
  // identity/nav role — the tournament-MANAGEMENT authority is resolved at the
  // b2b-account level via `hasTournamentPermission`; the role only flags "this
  // user is an approved organizer" for route-family + nav purposes.
  | 'tournament_organizer';

/**
 * Every value in the `UserRole` union, as a runtime tuple. Keep this in sync
 * with the union above (a drift here breaks the exhaustive RBAC registry which
 * is typed `Record<UserRole, …>`).
 */
export const USER_ROLES: readonly UserRole[] = [
  'consumer',
  'venue_owner',
  'trainer',
  'club_admin',
  'tournament_organizer',
] as const;

export type UserPermission =
  | 'profile:read'
  | 'profile:write'
  | 'profile:search'
  | 'account:delete'
  | 'verifications:request'
  | 'venues:suggest_edit'
  | 'venue_claim:submit'
  | 'venue_profile:read'
  | 'venue_profile:write'
  | 'venue_profile:upload_photo'
  | 'b2b:read'
  | 'b2b:write'
  | 'trainer_profile:read'
  | 'trainer_profile:write'
  | 'affiliation:read'
  | 'affiliation:write'
  // F16 — reservation-engine consumer-facing permissions. All four user
  // roles receive each of the three (a consumer reads/writes/cancels their
  // own bookings; a venue_owner / trainer / club_admin who is also a
  // consumer still books their own slots on the consumer flow). Venue-side
  // booking authZ flows through `B2bPermission` ('bookings:read',
  // 'bookings:accept', 'bookings:decline', 'bookings:cancel',
  // 'blockouts:write') via `hasVenuePermission()`, NOT this user-level set.
  | 'bookings:read'
  | 'bookings:write'
  | 'bookings:cancel_own'
  // F17 — booker self-service reschedule. Granted to all 4 user roles per
  // Decision 13 of the F17 plan. The booker authZ check inside
  // `BookingService.rescheduleBooking` also asserts `booking.host_user_id ===
  // actor` so the permission is "the booker may reschedule THEIR OWN
  // booking", same scope rule as `bookings:cancel_own`.
  | 'bookings:reschedule_own'
  // F20 — customer-side payment-marking permissions. Granted to all 4 user
  // roles (a venue_owner / trainer / club_admin who is also a consumer must
  // still be able to self-claim "I've paid" or file a dispute on bookings
  // they made as a consumer). The service-layer authZ check additionally
  // asserts `booking.host_user_id === actor` so these are "for THE BOOKER's
  // OWN booking" — same scope pattern as `bookings:cancel_own` /
  // `bookings:reschedule_own`. Per Decision 2 of the F20 plan,
  // `bookings:claim_payment` flips payment_status unpaid → pending_confirmation
  // (button-only, no input fields); venue staff confirm/reject. Per Decision 7,
  // `bookings:dispute_payment` requires payment_status === 'paid' at the
  // service layer and writes one `payment_disputes` row (DB-enforced
  // single-open-per-booking).
  | 'bookings:claim_payment'
  | 'bookings:dispute_payment'
  // F26 — in-app inbox + preferences + FCM device-token management. All 4
  // user roles get the three. `notifications:read` covers GET inbox + GET
  // unread-count + GET detail + GET preferences. `notifications:write` covers
  // POST mark-read / mark-all-read + DELETE dismiss + PATCH preferences.
  // `device_tokens:write` covers POST /me/device-tokens (register on first
  // permission grant) + PATCH (on FCM token refresh) + DELETE (on sign-out).
  // Service-layer authZ additionally scopes every action to the caller's own
  // user_id (no cross-user inbox reads — even by venue_owner staff).
  | 'notifications:read'
  | 'notifications:write'
  | 'device_tokens:write'
  // F27 — WhatsApp opt-in lifecycle. All 4 user roles get the same pair.
  // `whatsapp:read` gates GET /me/whatsapp/opt-in-status. `whatsapp:write`
  // gates POST /me/whatsapp/opt-in + DELETE /me/whatsapp/opt-in. The service
  // layer additionally scopes every action to the caller's own user_id
  // (no cross-user opt-in/out possible). ZERO new B2B permissions — venue
  // staff opt in via their own `users.phone` like any other user.
  | 'whatsapp:read'
  | 'whatsapp:write'
  // F29 — Teams. All 4 user roles get the same three. `teams:read` gates
  // GET /me/teams + GET /me/teams/:teamId + GET /public/teams/* (public reads
  // are anonymous-OK so this perm gates only the authenticated-only routes
  // like the member-visible audit log). `teams:write` gates POST /me/teams
  // (creating a team) + every captain-only mutating route; the captain-only
  // scope is enforced separately by the team-rbac.ts `teamPermission` config
  // on each route, NOT here. `teams:join` gates POST /me/teams/:teamId/join
  // (direct-join public teams) + POST /me/teams/:teamId/join-requests +
  // POST /me/team-invitations/accept.
  | 'teams:read'
  | 'teams:write'
  | 'teams:join'
  // F30 — Team Chat (scoped). All 4 user roles get the same pair. `chat:read`
  // gates GET /me/chat/rooms + GET /me/chat/rooms/:roomId + GET /me/chat/rooms/
  // :roomId/messages + GET /me/chat/messages/:id/read-by + GET /me/chat/polls/
  // :pollId. `chat:write` gates POST /me/chat/rooms/:roomId/messages + DELETE
  // /me/chat/messages/:id + POST /me/chat/rooms/:roomId/read + PATCH /me/chat/
  // rooms/:roomId/mute + POST /me/chat/rooms/:roomId/polls + POST /me/chat/
  // polls/:pollId/votes + every Socket.IO client→server event. Per-room
  // authorization (caller is an active chat_room_members row for this room) is
  // enforced separately by the chat-auth.ts plugin's `chatPermission` route
  // config, NOT here. Captain delete-any in team-rooms + booker delete-any in
  // booking-rooms is also enforced at the service layer (Decision D9).
  | 'chat:read'
  | 'chat:write'
  // F31 — Skill-Based Matching. All 4 user roles get the same three.
  // `match_outcomes:read` gates GET /me/match-outcomes[/:id] + GET
  // /me/skill-ratings[/:sport/history]. `match_outcomes:write` gates the
  // confirm / report / dispute / mark-presence mutations on your own outcomes
  // (participant-scope enforced at the service layer). `sport_skill_descriptors:read`
  // gates GET /me/sport-skill-descriptors/:sport (the inline level descriptions
  // on the new-sport self-rating screen).
  | 'match_outcomes:read'
  | 'match_outcomes:write'
  | 'sport_skill_descriptors:read'
  // F32 — Open Games Marketplace. All 4 user roles get the same three.
  // `open_games:read` gates GET /me/open-games/* (my posted games, my join
  // requests, alert subscriptions). `open_games:write` gates posting / editing /
  // closing / cancelling / approving / declining — per-game ownership is
  // enforced separately (team games via team-rbac.ts `open_games:manage`
  // (captain); solo-booking games via OpenGameAccessService (booking host)).
  // `open_games:join` gates request-to-join / withdraw / leave / report. Public
  // marketplace reads (`GET /public/open-games[/:id]`) are anonymous-OK.
  | 'open_games:read'
  | 'open_games:write'
  | 'open_games:join'
  // F33 — trainer marketplace. `trainers:book` is granted to all 4 user roles;
  // the rest are trainer-only (per-resource ownership checked at the service
  // layer). trainer_profile:read/write + affiliation:read/write came in F13.
  | 'trainers:book'
  | 'trainer_packages:write'
  | 'trainer_certs:write'
  | 'trainer_bookings:read'
  | 'trainer_bookings:mark_payment'
  // F34 — trainer dashboard (trainer-only; per-resource ownership checked at
  // the service layer: trainer_user_id === actor).
  | 'trainer_availability:read'
  | 'trainer_availability:write'
  | 'trainer_clients:read'
  | 'client_notes:read'
  | 'client_notes:write'
  | 'trainer_income:read'
  | 'trainer_packages:read'
  // F35 — trainer billing model (trainer-only; service asserts trainer_user_id === actor).
  | 'trainer_billing:read'
  | 'trainer_billing:write'
  // F36 — Trainer-Managed Teams (Club archetype). These are ROUTE-FAMILY gates
  // only; the SCOPED action (account-wide vs assigned-team) is enforced inside
  // each handler via `hasClubPermission` / `hasClubTeamPermission`. Granted to
  // `club_admin` + `trainer` (a trainer can hold a `club_trainer` membership);
  // players (`consumer`) get only `training:read` + `attendance:read` for the
  // read-only B2C surfaces (own training schedule + own attendance).
  | 'club:read'
  | 'club:write'
  | 'club_teams:read'
  | 'club_teams:write'
  | 'training:read'
  | 'training:write'
  | 'attendance:read'
  | 'attendance:mark'
  | 'friendly:read'
  | 'friendly:manage'
  | 'tournament:register'
  | 'player_notes:write'
  // F37 — Player ↔ Trainer Recommendations. Granted to ALL 4 user roles
  // (F32 precedent — the broad route-family grant is intentional; the REAL
  // authorization is the per-resource service gate: D5 verification for
  // reviews, D5/D10 eligibility for endorsements, owner-check for visibility.
  // A pure-consumer F36 club-trainer therefore passes the route gate and is
  // authorized by EndorsementEligibilityService). `reviews:write` gates submit/
  // edit/delete/flag; `reviews:read` gates "my reviews" + prompts; `endorsements:
  // write` gates award/revoke (a club-trainer endorses via this perm + the
  // eligibility service); `badges:read` gates the player's own gallery (incl.
  // hidden); `badges:manage_visibility` gates the per-badge hide/unhide toggle.
  | 'reviews:write'
  | 'reviews:read'
  | 'endorsements:write'
  | 'badges:read'
  | 'badges:manage_visibility'
  // F40 — Promotions & Discount Codes. Granted to ALL 4 user roles. Gates the
  // B2C `POST /me/promotions/evaluate` advisory-evaluate + auto-match preview
  // and the per-sport near-me alert opt-in (`GET/POST/DELETE /me/promotion-alerts`).
  // Route-family gate only; per-resource scoping (e.g. the alert subscription is
  // the caller's own) is enforced at the service layer.
  | 'promotions:browse'
  // F41 — Tournament & Event Listings. Granted to ALL 4 user roles (the F32
  // precedent — the broad route-family grant is intentional; the REAL authZ is
  // the per-resource service gate). `tournaments:browse` gates the B2C
  // marketplace + tournament-detail reads (the authenticated-only surfaces;
  // public reads are anonymous-OK). `tournaments:register` gates the
  // register / withdraw / pay-for-registration consumer flow; per-registration
  // ownership (registrant_user_id === actor) is enforced at the service layer.
  | 'tournaments:browse'
  | 'tournaments:register'
  // F43 — Admin User Management & Moderation. Two consumer-facing affordance
  // permissions granted to EVERY user role. `reports:file` gates
  // POST /me/reports (file a report against a user / chat_message / review /
  // badge / placement / promo / tournament / tournament_announcement subject;
  // one-open-per-(reporter,subject) is a service-layer + DB guard).
  // `gdpr:request` gates the optional consumer GDPR intake
  // (POST /me/gdpr-requests — export | delete). Both are route-family gates;
  // the real authZ (e.g. one-open-per-reporter, own-data-only) is enforced at
  // the service layer.
  | 'reports:file'
  | 'gdpr:request';
// Future features extend this union:
// F36 adds 'club:read'|'club:write'|…
// F08 added 'profile:search'.
// F09 added 'verifications:request' (all 4 roles).
// F10 added 'venues:suggest_edit' (all 4 roles — authenticated edit suggestions).
// F11 added 'venue_claim:submit' (consumer + venue_owner only — a venue_owner
// with one approved claim can still submit a SECOND claim for another venue,
// multi-venue case; trainer + club_admin have their own onboarding flows
// (F33/F36) and intentionally do NOT receive this perm — a trainer who wants
// to claim a venue must add the venue_owner role to their account, which
// F07's `roles text[]` supports).
// F12 added 'venue_profile:read', 'venue_profile:write',
// 'venue_profile:upload_photo' (venue_owner role only — gates the owner-only
// self-service profile editor at MVP; F13 widens with per-field staff RBAC).

// F43 — `'suspended'` added (D2): the admin-suspend status the F07 auth hook
// re-checks on every request (instant lockout of B2C + B2B). Distinct from the
// unused-terminal `'disabled'`. The `users_status_valid` CHECK + `UserService.mapRow`
// cast widen to match (migration 0039 + T1.2).
export type UserStatus = 'pending_verification' | 'active' | 'suspended' | 'disabled' | 'deleted';

/**
 * Profile-picture variant URLs (migration 0045). Both are public CloudFront
 * URLs into the `avatars` bucket. `thumbUrl` is 200x200 (lists/cards),
 * `cardUrl` is 400x400 (headers). `User.avatarUrl` mirrors `cardUrl`.
 */
export interface AvatarVariants {
  readonly thumbUrl: string;
  readonly cardUrl: string;
}

export interface User {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly phone: string | null;
  readonly phoneVerifiedAt: string | null;
  readonly emailVerifiedAt: string | null;
  readonly googleSubject: string | null;
  readonly roles: readonly UserRole[];
  readonly status: UserStatus;
  readonly countryCode: 'LB';
  readonly locale: Locale;
  readonly termsAcceptedVersion: string | null;
  readonly privacyAcceptedVersion: string | null;
  readonly termsAcceptedAt: string | null;
  readonly lastLoginAt: string | null;
  /** Card-size (400x400) avatar CDN URL, or null when no avatar is set. */
  readonly avatarUrl: string | null;
  /** {thumbUrl, cardUrl} variant URLs, or null when no avatar is set. */
  readonly avatarVariants: AvatarVariants | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UserSummary {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly roles: readonly UserRole[];
  readonly status: UserStatus;
}

/**
 * Shape returned by `GET /me`. Spreads `User` and augments it with
 * F08-derived booleans and F09 verification fields.
 *
 * F08 added `onboardingCompleted` — `true` iff the user's
 * `sports_profiles.onboarding_completed_at IS NOT NULL`.
 *
 * F09 added:
 *   - `verifiedAt` — ISO timestamp of when the blue tick was granted; null if not verified.
 *   - `verifiedType` — the v1 verification category (always null since F09 v2).
 *   - `verificationBlockedUntil` — the v1 cooldown end (always null since F09 v2).
 *
 * F09 v2 (E20 — ADDITIVE only): `verified` (the PLAYER tick) and
 * `displayNameLock` (locked if the player OR trainer tick is held — E4/E26).
 * `verifiedType` + `verificationBlockedUntil` stay on the wire (E20 additive-only)
 * but are typed `null` — they are always null.
 *
 * Returning this aggregated shape (rather than a separate `/me/onboarding`
 * call) avoids a round-trip on every cold start of the consumer apps.
 */
export interface MeResponse extends User {
  readonly onboardingCompleted: boolean;
  /** The player tick's display-only "since" date (`users.verified_at` — player tick only in v2). */
  readonly verifiedAt: string | null;
  /** @deprecated F09 v2 (E20) — always null. Use `verified` / `GET /me/verification-targets`. */
  readonly verifiedType: null;
  /** @deprecated F09 v2 (E20) — always null. Cooldowns live on `OwnerEntityVerificationState`. */
  readonly verificationBlockedUntil: null;
  /** F09 v2 — the PLAYER tick (`users.verification_status = 'verified'`). */
  readonly verified: boolean;
  /** F09 v2 — `users.display_name` lock (player OR trainer tick; `lockedBy[0]` = player if held). */
  readonly displayNameLock: DisplayNameLock;
}

export interface UserJwtClaims {
  readonly sub: string;
  readonly subjectType: 'user';
  readonly roles: readonly UserRole[];
  readonly permissions: readonly UserPermission[];
  readonly email: string;
  readonly countryCode: 'LB';
  readonly locale: Locale;
  readonly aud: string; // env.JWT_AUDIENCE_USER, default 'consumer'
  readonly iat: number;
  readonly exp: number;
  readonly jti: string;
  readonly kind: 'access' | 'otp_challenge';
  readonly purpose?: 'signup' | 'login'; // only on otp_challenge tokens
}

export interface SignupRequest {
  readonly email: string;
  readonly displayName: string;
  readonly role: UserRole;
  readonly phone?: string;
  readonly termsAcceptedVersion: string;
  readonly privacyAcceptedVersion: string;
  readonly locale?: Locale;
}

export interface LoginRequest {
  readonly email: string;
}

export interface VerifyOtpRequest {
  readonly otpChallengeToken: string;
  readonly code: string;
}

export interface ResendOtpRequest {
  readonly otpChallengeToken: string;
}

export interface GoogleLoginRequest {
  readonly idToken: string;
  readonly role?: UserRole;
  readonly displayName?: string;
  readonly phone?: string;
  readonly termsAcceptedVersion?: string;
  readonly privacyAcceptedVersion?: string;
  readonly locale?: Locale;
}

export interface AcceptInviteRequest {
  readonly token: string;
  readonly termsAcceptedVersion: string;
  readonly privacyAcceptedVersion: string;
  readonly phone?: string;
  readonly displayName?: string;
}

export interface OtpChallengeResponse {
  readonly otpChallengeToken: string;
  readonly otpChallengeExpiresAt: string;
  readonly emailSentTo: string;
}

export interface UserSessionResponse {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly user: User;
  /**
   * F29 D5 — `POST /auth/verify-otp` only, when a signup carried a
   * `teamInviteToken`: the server joins the new user to the team and reports
   * the outcome (a failed join never blocks activation). Absent otherwise.
   */
  readonly teamJoin?:
    | {
        readonly ok: true;
        readonly teamId: string;
        readonly memberId: string;
        readonly idempotentReplay: boolean;
      }
    | { readonly ok: false; readonly code: string };
}

export interface UserInvite {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly targetRole: Exclude<UserRole, 'consumer'>;
  readonly targetOrgType: 'venue' | 'club' | null;
  readonly targetOrgId: string | null;
  readonly invitedByUserId: string | null;
  readonly invitedByAdminId: string | null;
  readonly expiresAt: string;
  readonly consumedAt: string | null;
  readonly consumedByUserId: string | null;
  readonly createdAt: string;
}

// ─── F43 Admin User Management & Moderation shapes ────────────────────────────

/** Bound for the suspend/restore reason (≤500, mirrors the schema CHECK). */
export const SUSPENSION_REASON_MAX = 500;

/** Cap on per-op bulk targets (>cap → BULK_LIMIT_EXCEEDED). */
export const BULK_ACTION_MAX_TARGETS = 100;

/**
 * F09 v2 (E41) — the admin users-list verification filter: `any` = holds a
 * player OR trainer tick; `player` = holds the player tick.
 */
export const ADMIN_USER_VERIFICATION_FILTERS = ['any', 'player'] as const;
export type AdminUserVerificationFilter = (typeof ADMIN_USER_VERIFICATION_FILTERS)[number];

/**
 * One row in the `GET /admin/users` table (search + filter). A compact identity
 * + status + activity-signal projection. `hasActiveReportsAgainst` powers the
 * "has active reports" filter; `playerVerified` / `trainerVerified` carry the
 * F09 v2 per-entity ticks.
 */
export interface AdminUserListItem {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly phone: string | null;
  readonly roles: readonly UserRole[];
  readonly status: UserStatus;
  /** Display-only "since" date of the PLAYER tick (null when not verified). */
  readonly verifiedAt: string | null;
  /** F09 v2 (E41) — holds the player tick. */
  readonly playerVerified: boolean;
  /** F09 v2 (E41) — holds the trainer tick. */
  readonly trainerVerified: boolean;
  readonly suspendedAt: string | null;
  readonly hasActiveReportsAgainst: boolean;
  readonly countryCode: 'LB';
  readonly createdAt: string;
}

/**
 * The `GET /admin/users/:id` detail header. The per-tab payloads (bookings,
 * teams, affiliations, trainer, reviews/badges, reports, GDPR, audit, devices)
 * are loaded via sub-resources and intentionally kept loose/optional here — each
 * tab is a read-through into the owning feature (F17/F29/F36/F33-35/F37/F26).
 * `updatedAt` is the If-Match anchor for suspend/restore writes.
 */
export interface AdminUserDetail {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly phone: string | null;
  readonly phoneVerifiedAt: string | null;
  readonly emailVerifiedAt: string | null;
  readonly googleSubject: string | null;
  readonly roles: readonly UserRole[];
  readonly status: UserStatus;
  /** Display-only "since" date of the PLAYER tick (null when not verified). */
  readonly verifiedAt: string | null;
  /** F09 v2 (E41) — holds the player tick. */
  readonly playerVerified: boolean;
  /** F09 v2 (E41) — holds the trainer tick. */
  readonly trainerVerified: boolean;
  readonly suspendedAt: string | null;
  readonly suspendedByAdminId: string | null;
  readonly suspensionReason: string | null;
  readonly restoredAt: string | null;
  readonly restoredByAdminId: string | null;
  readonly countryCode: 'LB';
  readonly locale: Locale;
  readonly lastLoginAt: string | null;
  /** Card-size (400x400) avatar CDN URL, or null when no avatar is set. */
  readonly avatarUrl: string | null;
  /** {thumbUrl, cardUrl} variant URLs, or null when no avatar is set. */
  readonly avatarVariants: AvatarVariants | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * POST /admin/users/:id/suspend — mandatory reason. The `If-Match` header (the
 * `users.updated_at` ISO) rides in the request header, not the body (F12 pattern).
 */
export const SuspendUserInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(SUSPENSION_REASON_MAX),
  })
  .strict();
export type SuspendUserInput = z.infer<typeof SuspendUserInputSchema>;

/**
 * Super-admin delete-user input (DELETE /admin/platform-users/:id). A soft
 * delete (status='deleted' + deleted_at) gated `users:delete`; reason is
 * optional + captured in the admin audit row. If-Match rides in the header.
 */
export const DeleteUserInputSchema = z
  .object({
    reason: z.string().trim().max(SUSPENSION_REASON_MAX).optional(),
  })
  .strict();
export type DeleteUserInput = z.infer<typeof DeleteUserInputSchema>;

/**
 * PATCH /admin/platform-users/:id/fields — the super-admin "edit everything"
 * payload (gated by the `users:edit` admin permission). All fields optional
 * (PATCH = changed fields only); `.strict()` rejects unknown keys. The
 * `If-Match: <users.updated_at ISO>` header rides in the request header (F12
 * two-phase concurrency). Server-side normalization + safety:
 *   - `email` is lower-cased + uniqueness-checked; changing it clears `email_verified_at`.
 *   - `phone` is E.164-normalized via `parsePhone` (or `null` to clear it).
 *   - `roles` must be non-empty.
 *   - `status` transitions to/from 'suspended' route through the suspend/restore
 *     cascade (session + device-token revoke, sole-owned B2B cascade) so a raw
 *     status flip never leaves a half-suspended account.
 */
export const AdminEditUserInputSchema = z
  .object({
    displayName: z.string().trim().min(1).max(120),
    email: z.string().trim().email().max(254),
    phone: z.string().trim().min(1).nullable(),
    roles: z.array(z.enum([...USER_ROLES] as [UserRole, ...UserRole[]])).min(1),
    status: z.enum(['pending_verification', 'active', 'suspended', 'disabled', 'deleted']),
    locale: z.enum(['en', 'ar', 'fr']),
    countryCode: z.string().trim().length(2),
  })
  .partial()
  .strict();
export type AdminEditUserInput = z.infer<typeof AdminEditUserInputSchema>;

/**
 * Admin Super-Powers — POST /admin/platform-users (super_admin, `users:create`).
 * Mints a brand-new CONSUMER user in `pending_verification` + issues an
 * activation invite (an email link redeemed via /auth/invite/accept). Business
 * roles are NOT settable here — they attach only by creating a B2B actor
 * (POST /admin/b2b-accounts), keeping "every business role has a backing
 * account". `.strict()` rejects unknown keys. Server stores no terms acceptance
 * at create — the user accepts terms at activation.
 */
export const AdminCreateUserInputSchema = z
  .object({
    email: z.string().trim().email().max(254),
    displayName: z.string().trim().min(1).max(120),
    phone: z.string().trim().min(1).optional(),
    locale: z.enum(['en', 'ar', 'fr']).optional(),
    countryCode: z.string().trim().length(2).optional(),
  })
  .strict();
export type AdminCreateUserInput = z.infer<typeof AdminCreateUserInputSchema>;

/**
 * POST /me/avatar (self-service) AND POST /admin/platform-users/:id/avatar
 * (super-admin) — request a presigned upload URL. `POST .../avatar/finalize`
 * then carries `{ s3Key }`.
 */
export const AvatarUploadUrlInputSchema = z
  .object({
    mimeType: z.string().trim().min(1),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(5 * 1024 * 1024),
  })
  .strict();
export type AvatarUploadUrlInput = z.infer<typeof AvatarUploadUrlInputSchema>;

export const AvatarFinalizeInputSchema = z.object({ s3Key: z.string().min(1) }).strict();
export type AvatarFinalizeInput = z.infer<typeof AvatarFinalizeInputSchema>;

/**
 * POST /admin/bulk/{suspend,dismiss-reports,reassign-reports} — ≤100 targets,
 * mandatory reason. `Idempotency-Key` rides in the request header. `assignedAdminId`
 * is required only for the reassign-reports op (validated at the route layer).
 */
export const BulkActionInputSchema = z
  .object({
    targetIds: z.array(z.string().uuid()).min(1).max(BULK_ACTION_MAX_TARGETS),
    reason: z.string().trim().min(1).max(SUSPENSION_REASON_MAX),
    assignedAdminId: z.string().uuid().optional(),
  })
  .strict();
export type BulkActionInput = z.infer<typeof BulkActionInputSchema>;

/** Per-item outcome captured in `bulk_action_records.results` (partial success). */
export interface BulkActionResult {
  readonly targetId: string;
  readonly status: 'ok' | 'failed' | 'skipped';
  readonly reason?: string;
}

/** The `POST /admin/bulk/*` response envelope. */
export interface BulkActionResponse {
  readonly requestedCount: number;
  readonly succeededCount: number;
  readonly failedCount: number;
  readonly results: readonly BulkActionResult[];
}
